import { useAuth } from '@src/data/settings';
import { trpcReact } from '@src/data/trpc';
import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useDebounce } from 'use-debounce';
import { useEffectiveAccount } from './useEffectiveAccount';

export const useFriendsDailyMysteryCityPerformance = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);

  return trpcReact.getFriendsDailyMysteryCityPerformance.useQuery(
    {
      address: effectiveAddress!,
      auth,
      challengeAuth,
      network: import.meta.env.VITE_NETWORK,
    },
    {
      enabled: !!effectiveAddress && (!!auth || !!challengeAuth),
    }
  );
};

export const useFriends = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);

  return trpcReact.getFriends.useQuery(
    {
      address: effectiveAddress!,
      auth,
      challengeAuth,
    },
    {
      enabled: !!effectiveAddress && (!!auth || !!challengeAuth),
    }
  );
};

export const useAddFriend = (newFriendUsername?: string) => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);
  const trpcReactUtils = trpcReact.useUtils();

  const [debouncedFriendUsername] = useDebounce(newFriendUsername, 300);

  const { data: isValidFriendUsername, isLoading: isLoadingValidity } =
    trpcReact.isValidFriendUsername.useQuery(
      {
        username: debouncedFriendUsername!,
      },
      {
        enabled: !!debouncedFriendUsername,
      }
    );

  const { mutate, isLoading, isError, error, isSuccess } =
    trpcReact.addFriend.useMutation({
      onSuccess: () => {
        toast.success('Added new friend!');
        trpcReactUtils.getFriendsDailyMysteryCityPerformance.invalidate({
          auth,
          address: effectiveAddress!,
          challengeAuth,
          network: import.meta.env.VITE_NETWORK,
        });
        trpcReactUtils.getFriends.invalidate({
          auth,
          address: effectiveAddress!,
          challengeAuth,
        });
      },
      onError: (e) => {
        toast.error(`Couldn't update referral code: ${e.message}`);
      },
    });

  const addFriend = useCallback(() => {
    let errorMsg;
    if (!isValidFriendUsername) return;
    if (!newFriendUsername || newFriendUsername.trim() === '') {
      errorMsg = 'No new referral code entered!';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
    if (!/^[a-zA-Z0-9_]{3,15}$/.test(newFriendUsername)) {
      errorMsg =
        'Username must be 3-15 characters long and contain only letters, numbers, and underscores';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
    if (!effectiveAddress) {
      errorMsg = 'Not connected';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    } else if (!(!!auth || !!challengeAuth)) {
      errorMsg = 'No auth found';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    mutate({
      address: effectiveAddress,
      friendUsername: newFriendUsername,
      auth,
      challengeAuth,
    });
  }, [newFriendUsername, isValidFriendUsername]);

  return {
    addFriend,
    isError,
    error,
    isLoading,
    isSuccess,
    isValidFriendUsername,
    isLoadingValidity,
  };
};

export const useRemoveFriend = (friendUsername?: string) => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);
  const trpcReactUtils = trpcReact.useUtils();

  const { mutate, isLoading, isError, error, isSuccess } =
    trpcReact.removeFriend.useMutation({
      onSuccess: () => {
        toast.success(`Removed ${friendUsername}!`);
        trpcReactUtils.getFriendsDailyMysteryCityPerformance.invalidate({
          auth,
          address: effectiveAddress!,
          challengeAuth,
          network: import.meta.env.VITE_NETWORK,
        });
        trpcReactUtils.getFriends.invalidate({
          auth,
          address: effectiveAddress!,
          challengeAuth,
        });
      },
      onError: (e) => {
        toast.error(`Couldn't remove ${friendUsername}: ${e.message}`);
      },
    });

  const removeFriend = useCallback(() => {
    let errorMsg;
    if (!friendUsername || friendUsername.trim() === '') {
      errorMsg = 'No friend username found!';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
    if (!/^[a-zA-Z0-9_]{3,15}$/.test(friendUsername)) {
      errorMsg =
        'Username must be 3-15 characters long and contain only letters, numbers, and underscores';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
    if (!effectiveAddress) {
      errorMsg = 'Not connected';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    } else if (!(!!auth || !!challengeAuth)) {
      errorMsg = 'No auth found';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    mutate({
      address: effectiveAddress,
      friendUsername: friendUsername,
      auth,
      challengeAuth,
    });
  }, [friendUsername]);

  return {
    removeFriend,
    isError,
    error,
    isLoading,
    isSuccess,
  };
};
